import type { FetchAPI } from '../../api-contracts/runtime';
import { logger } from '../../utils/logger';

interface SessionPort {
  getToken(): string | null;
  refresh(): Promise<void>;
}

export interface RequestTransportDeps {
  fetch: FetchAPI;
  session: SessionPort;
  now: () => number;
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
}

/** No response headers arrived in time. Like a network error, it isn't counted toward Needs attention. */
export class RequestTimeoutError extends Error {
  constructor(ms: number) {
    super(`No response within ${ms / 1000} s`);
    this.name = 'RequestTimeoutError';
  }
}

const TIMEOUT_MS = 60_000;
const SLOW_MS = 3000;
const RETRY_WAITS_MS = [1000, 3000];
const MAX_RETRY_AFTER_S = 10;
const RETRIED_METHODS = new Set(['GET', 'HEAD']);
const RETRIED_STATUSES = new Set([408, 429, 502, 503, 504]);

/** The wait before retrying `response`, or null when it isn't retried. */
const retryWait = (response: Response, attempt: number): number | null => {
  if (!RETRIED_STATUSES.has(response.status)) return null;
  const retryAfter = response.headers.get('Retry-After');
  if (retryAfter === null) return RETRY_WAITS_MS[attempt];
  if (!/^\d+$/.test(retryAfter.trim())) return null;
  const seconds = Number(retryAfter);
  return seconds <= MAX_RETRY_AFTER_S ? seconds * 1000 : null;
};

/** Releases the connection held by an answer that is replaced by another attempt. */
const discardBody = (response: Response) => {
  response.body?.cancel().catch((error: unknown) => {
    logger.debug(
      `Discarding a replaced response body failed: ${String(error)}`,
    );
  });
};

const logResponse = (
  method: string,
  input: RequestInfo | URL,
  status: number,
  ms: number,
) => {
  const url = input instanceof Request ? input.url : String(input);
  const path = url.replace(/^[a-z][a-z\d+.-]*:\/\/[^/]*/i, '').split('?')[0];
  const line = `API ${method} ${path} ${status} (${Math.round(ms)} ms)`;
  if (status >= 400 || ms > SLOW_MS) logger.warn(line);
  else logger.debug(line);
};

export function createRequestTransport(deps: RequestTransportDeps): FetchAPI {
  const { session } = deps;
  let refreshing: Promise<void> | null = null;

  // Concurrent 401s share one refresh. The refresh's own outcome isn't trusted (it can
  // report success after failing), so the caller compares the token before and after.
  const refreshOnce = () => {
    refreshing ??= session.refresh().finally(() => (refreshing = null));
    return refreshing;
  };

  const send = async (
    method: string,
    input: RequestInfo | URL,
    init: RequestInit,
    token: string | null,
  ) => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const controller = new AbortController();
    const callerSignal = init.signal;
    if (callerSignal?.aborted) controller.abort(callerSignal.reason);
    callerSignal?.addEventListener(
      'abort',
      () => controller.abort(callerSignal.reason),
      { once: true },
    );

    // The timeout covers the wait for headers only, so a body can stream for as long as it takes.
    // The timer's rejection is it being cancelled because the request settled: nothing to do.
    const headersArrived = new AbortController();
    deps.sleep(TIMEOUT_MS, headersArrived.signal).then(
      () => controller.abort(new RequestTimeoutError(TIMEOUT_MS)),
      () => undefined,
    );

    const started = deps.now();
    try {
      const response = await deps.fetch(input, {
        ...init,
        headers,
        signal: controller.signal,
      });
      logResponse(method, input, response.status, deps.now() - started);
      return response;
    } catch (error) {
      const reason: unknown = controller.signal.reason;
      throw reason instanceof RequestTimeoutError ? reason : error;
    } finally {
      headersArrived.abort();
    }
  };

  /** A token other than `sent`, renewing it only when no other request already has. */
  const tokenAfter401 = async (sent: string | null) => {
    const current = session.getToken();
    if (current && current !== sent) return current;
    try {
      await refreshOnce();
    } catch (error) {
      logger.warn(
        `Token refresh after a 401 failed: ${error instanceof Error ? error.message : String(error)}`,
      );
      return null;
    }
    const renewed = session.getToken();
    return renewed && renewed !== sent ? renewed : null;
  };

  /**
   * Sends once, and once more with a renewed token when the first is refused with a 401.
   * `auth.retried` spans every attempt of one request, so a request retries a 401 once in all.
   */
  const sendSignedIn = async (
    method: string,
    input: RequestInfo | URL,
    init: RequestInit,
    auth: { retried: boolean },
  ) => {
    const token = session.getToken();
    const response = await send(method, input, init, token);
    if (response.status !== 401 || auth.retried) return response;
    auth.retried = true;
    const renewed = await tokenAfter401(token);
    if (!renewed) return response;
    discardBody(response);
    return send(method, input, init, renewed);
  };

  return async (input, init = {}) => {
    const method = (init.method ?? 'GET').toUpperCase();
    const retries = RETRIED_METHODS.has(method) ? RETRY_WAITS_MS.length : 0;
    const auth = { retried: false };

    for (let attempt = 0; ; attempt++) {
      let wait: number | null;
      try {
        const response = await sendSignedIn(method, input, init, auth);
        wait = attempt < retries ? retryWait(response, attempt) : null;
        if (wait === null) return response;
        discardBody(response);
      } catch (error) {
        if (attempt >= retries || init.signal?.aborted) throw error;
        wait = RETRY_WAITS_MS[attempt];
      }
      await deps.sleep(wait, init.signal ?? undefined);
    }
  };
}
